/**
 * features/leads/PublicLeadForm.tsx
 * Public, unauthenticated enquiry form. Submits via the submitLead
 * callable, which writes the lead and emails the operator + a
 * thank-you. Includes a hidden honeypot field for basic bot defence.
 */
import { useState } from 'react';
import { Send, Check } from 'lucide-react';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { submitLead } from './leadsWrite';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PublicLeadForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [hp, setHp] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const valid = name.trim().length > 0 && EMAIL_RE.test(email.trim());

  async function send() {
    if (!valid) {
      setError('Please enter your name and a valid email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await submitLead({ name: name.trim(), email: email.trim(), phone: phone.trim(), company: company.trim(), message: message.trim(), hp });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-ground dark:bg-groundDark">
      <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen max-w-lg items-center px-4 py-10">
        <div className="w-full">
          {/* Wordmark */}
          <div className="mb-6 flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="h-7 w-7 text-brand" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 5 L9 12 L2 19" />
            </svg>
            <span className="text-xl font-bold tracking-tight text-primary dark:text-gray-100" style={{ fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Acepsle
            </span>
          </div>

          {done ? (
            <div className={glassSurface('p-8 text-center')}>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30">
                <Check size={28} />
              </div>
              <h1 className="text-lg font-semibold text-primary dark:text-gray-100">Thank you!</h1>
              <p className="mt-2 text-sm text-primaryLight dark:text-gray-400">
                We’ve received your enquiry and will be in touch shortly. A confirmation is on its way to your inbox.
              </p>
            </div>
          ) : (
            <div className={glassSurface('space-y-4 p-6')}>
              <div>
                <h1 className="text-xl font-bold text-primary dark:text-gray-100">Get in touch</h1>
                <p className="mt-1 text-sm text-primaryLight dark:text-gray-400">
                  Tell us a little about what you need and we’ll get back to you.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Your name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
                </Field>
                <Field label="Phone (optional)">
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+267 …" />
                </Field>
                <Field label="Company (optional)">
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} />
                </Field>
              </div>
              <Field label="How can we help?">
                <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short description…" />
              </Field>

              {/* Honeypot — hidden from humans */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <Button icon={<Send size={16} />} onClick={send} loading={busy} disabled={!valid} className="w-full justify-center">
                Send enquiry
              </Button>
              <p className="text-center text-xs text-primaryLight dark:text-gray-500">
                We use these details only to reply to your enquiry &mdash; see our{' '}
                <a href="/privacy" className="underline hover:text-brand">privacy policy</a>.
                <br />
                Acepsle Proprietary Limited · Gaborone, Botswana
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
